import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import {
  buildWordsFromPairs,
  generateLayout,
  getWordStartPositions,
  serializeCrosswordLayout,
} from "./crosswordLayout";
import { clampQuizFields, validateQuizList } from "./quizHelpers";
import QuizTab from "./QuizTab";
import DosAndDontTab from "./DosAndDontTab";
import { normalizeStatementsFromTheme, validateStatementsList } from "./DosAndDontTab";
import WordPuzzleTab, {
  normalizeWordEntriesFromTheme,
  parseHintDisplayMode,
  validateWordPuzzleEntries,
} from "./WordPuzzleTab";
import StageRulesEditor, {
  normalizeRulesFromTheme,
  STAGE_HEADING_MAX_LEN,
  STAGE_SUBHEADING_MAX_LEN,
  validateStageHeadingSubheading,
  validateStageHeadingSubheadingAndRules,
  validateStageRules,
} from "./StageRulesEditor";
import { getUnscrambleMinAnswersForAdmin } from "../../../pages/game/unscrambleUtils";

const GAME_TABS = [
  { id: "downAndAcross", label: "Down and Across" },
  { id: "wordpuzzle", label: "Word Puzzle" },
  { id: "unscramble", label: "Unscramble" },
  { id: "quiz", label: "Quiz" },
  { id: "dosAndDont", label: "Dos and Dont" },
];

const DEFAULT_GAME_TAB = GAME_TABS[0].id;
const GAME_TAB_PARAM = "tab";

function getGameTabFromSearch(params) {
  const tab = params.get(GAME_TAB_PARAM);
  return GAME_TABS.some((t) => t.id === tab) ? tab : DEFAULT_GAME_TAB;
}

const MAX_PAIRS = 6;
const MIN_PAIRS = 2;
const QUESTION_MAX_LEN = 80;
const ANSWER_MAX_LEN = 15;

const UNSCRAMBLE_MAX_PAIRS = 6;
const UNSCRAMBLE_MIN_PAIRS = 2;
const UNSCRAMBLE_QA_MAX_LEN = 15;
const UNSCRAMBLE_CLUE_MAX_LEN = 50;
const UNSCRAMBLE_MIN_ANSWERS_MAX = UNSCRAMBLE_MAX_PAIRS;

const DEFAULT_SUBTITLE_STAGE_1 = "Complete the crossword by solving the clues given below.";
const DEFAULT_SUBTITLE_STAGE_2 =
  "Find the given words in the grid. (Words may be horizontal, vertical or diagonal)";
const DEFAULT_SUBTITLE_STAGE_3 =
  "Unscramble the words to discover key skills that help women unlock opportunities and grow personally and professionally.";
const DEFAULT_SUBTITLE_STAGE_4 = "Choose the option that best reflects your perspective.";
const DEFAULT_SUBTITLE_STAGE_5 =
  "Identify the do's and don'ts from the statements below in building your strong brand presence.";

const clampStageHeading = (raw) => String(raw ?? "").slice(0, STAGE_HEADING_MAX_LEN);
const clampStageSubheading = (raw) => String(raw ?? "").slice(0, STAGE_SUBHEADING_MAX_LEN);

let pairIdCounter = 0;
function newPairId(prefix = "pair") {
  pairIdCounter += 1;
  return `${prefix}-${pairIdCounter}`;
}

function sanitizeAlphaNum(value) {
  return String(value ?? "").replace(/[^a-zA-Z0-9\s]/g, "");
}

function emptyPair() {
  return { id: newPairId("da"), question: "", answer: "" };
}

function emptyUnscramblePair() {
  return { id: newPairId("un"), question: "", answer: "", clue: "" };
}

function normalizePairsFromTheme(raw) {
  const list = Array.isArray(raw) ? raw : [];
  if (!list.length) {
    return [emptyPair(), emptyPair()];
  }
  return list.map((item) => ({
    id: newPairId("da"),
    question: String(item?.question ?? "").slice(0, QUESTION_MAX_LEN),
    answer: String(item?.answer ?? "").slice(0, ANSWER_MAX_LEN),
  }));
}

function normalizeUnscrambleFromTheme(raw) {
  const list = Array.isArray(raw) ? raw : [];
  if (!list.length) {
    return [emptyUnscramblePair(), emptyUnscramblePair()];
  }
  return list.map((item) => ({
    id: newPairId("un"),
    question: sanitizeAlphaNum(item?.question).slice(0, UNSCRAMBLE_QA_MAX_LEN),
    answer: sanitizeAlphaNum(item?.answer).slice(0, UNSCRAMBLE_QA_MAX_LEN),
    clue: sanitizeAlphaNum(item?.clue).slice(0, UNSCRAMBLE_CLUE_MAX_LEN),
  }));
}

function parseShowClue(value) {
  return value === true || value === "true";
}

function normalizeQuizFromTheme(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => ({
    id: newPairId("quiz"),
    ...clampQuizFields(item),
  }));
}

function CrosswordPreview({ layout }) {
  if (!layout?.table?.length) {
    return (
      <p className="themeupdate-muted crossword-preview-empty">
        Add at least two complete question/answer pairs to preview the grid.
      </p>
    );
  }

  const { table, result: words, rows, cols } = layout;
  const wordStartPositions = getWordStartPositions(words);

  return (
    <div className="crossword-preview-wrap">
      <div
        id="crossword"
        className="crossword-grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {Array.from({ length: rows }, (_, i) =>
          Array.from({ length: cols }, (_, j) => {
            const letter = table[i][j];
            const isBlock = letter === "-";
            const positionKey = `${i + 1},${j + 1}`;
            const cellNumber = wordStartPositions[positionKey];

            return (
              <div
                key={`${i}-${j}`}
                className={`crossword-cell${isBlock ? " crossword-cell--block" : ""}`}
              >
                {!isBlock ? (
                  <>
                    {cellNumber ? (
                      <span className="crossword-cell-number">{cellNumber}</span>
                    ) : null}
                    <span className="crossword-cell-letter">{letter.toUpperCase()}</span>
                  </>
                ) : null}
              </div>
            );
          })
        )}
      </div>
      <div className="crossword-clues">
        <div className="crossword-clues-section">
          <h4>Across</h4>
          {words
            .filter((w) => w.orientation === "across")
            .map((w) => (
              <p key={`across-${w.starty}-${w.startx}-${w.answer}`} className="crossword-clue">
                {wordStartPositions[`${w.starty},${w.startx}`]}. {w.clue}
              </p>
            ))}
        </div>
        <div className="crossword-clues-section">
          <h4>Down</h4>
          {words
            .filter((w) => w.orientation === "down")
            .map((w) => (
              <p key={`down-${w.starty}-${w.startx}-${w.answer}`} className="crossword-clue">
                {wordStartPositions[`${w.starty},${w.startx}`]}. {w.clue}
              </p>
            ))}
        </div>
      </div>
    </div>
  );
}

function DownAndAcrossTab({
  pairs,
  setPairs,
  layout,
  layoutError,
  isSaving,
  onSave,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
}) {
  const addPair = () => {
    if (pairs.length >= MAX_PAIRS) {
      setErrorMessage(`You can add up to ${MAX_PAIRS} pairs.`);
      return;
    }
    setErrorMessage("");
    setPairs((prev) => [...prev, emptyPair()]);
  };

  const removePair = (id) => {
    if (pairs.length <= MIN_PAIRS) {
      setErrorMessage(`You need at least ${MIN_PAIRS} pairs.`);
      return;
    }
    setPairs((prev) => prev.filter((p) => p.id !== id));
    setErrorMessage("");
  };

  const updatePair = (id, field, value) => {
    const max = field === "question" ? QUESTION_MAX_LEN : ANSWER_MAX_LEN;
    setPairs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value.slice(0, max) } : p))
    );
    setErrorMessage("");
    setSuccessMessage("");
  };

  const clearAll = () => {
    setPairs([emptyPair(), emptyPair()]);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const leftPairs = pairs.filter((_, i) => i % 2 === 0);
  const rightPairs = pairs.filter((_, i) => i % 2 === 1);

  const renderPairCard = (pair) => (
    <div key={pair.id} className="qa-pair-card">
      <label htmlFor={`q-${pair.id}`}>Question</label>
      <input
        id={`q-${pair.id}`}
        type="text"
        value={pair.question}
        maxLength={QUESTION_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "question", e.target.value)}
        placeholder="Question"
      />
      <button
        type="button"
        className="qa-pair-delete"
        onClick={() => removePair(pair.id)}
        aria-label="Delete pair"
        title="Delete pair"
      >
        <i className="fa-regular fa-trash-can" />
      </button>
      <label htmlFor={`a-${pair.id}`}>Answer</label>
      <input
        id={`a-${pair.id}`}
        type="text"
        value={pair.answer}
        maxLength={ANSWER_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
        placeholder="Answer"
      />
    </div>
  );

  return (
    <div className="themeupdate-panel-wide down-across-panel">
      <p className="qa-char-limit-hint">
        * Add {MIN_PAIRS}–{MAX_PAIRS} complete pairs. Maximum character limit: Question —{" "}
        {QUESTION_MAX_LEN} characters, Answer — {ANSWER_MAX_LEN} characters.
      </p>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="qa-pairs-columns">
        <div className="qa-pairs-col">{leftPairs.map(renderPairCard)}</div>
        <div className="qa-pairs-col">{rightPairs.map(renderPairCard)}</div>
      </div>

      <div className="qa-pairs-actions">
        <button
          type="button"
          className="qa-btn qa-btn-outline"
          onClick={addPair}
          disabled={pairs.length >= MAX_PAIRS}
        >
          Add Pair
        </button>
        <button
          type="button"
          className="qa-btn qa-btn-primary"
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save and Continue"}
        </button>
        <button type="button" className="qa-btn qa-btn-primary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      <div className="themeupdate-card crossword-preview-card">
        <div className="themeupdate-card-header">Grid preview</div>
        <div className="themeupdate-card-body">
          {layoutError ? <p className="cr-alert cr-alert-error">{layoutError}</p> : null}
          <CrosswordPreview layout={layout} />
        </div>
      </div>
    </div>
  );
}

function UnscrambleTab({
  pairs,
  setPairs,
  showClue,
  setShowClue,
  minAnswersRequired,
  setMinAnswersRequired,
  isSaving,
  onSavePairs,
  onSaveSettings,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
}) {
  const addPair = () => {
    if (pairs.length >= UNSCRAMBLE_MAX_PAIRS) {
      setErrorMessage(`You can add up to ${UNSCRAMBLE_MAX_PAIRS} pairs.`);
      return;
    }
    setErrorMessage("");
    setPairs((prev) => [...prev, emptyUnscramblePair()]);
  };

  const removePair = (id) => {
    if (pairs.length <= UNSCRAMBLE_MIN_PAIRS) {
      setErrorMessage(`You need at least ${UNSCRAMBLE_MIN_PAIRS} pairs.`);
      return;
    }
    setPairs((prev) => prev.filter((p) => p.id !== id));
    setErrorMessage("");
  };

  const updatePair = (id, field, value) => {
    const max = field === "clue" ? UNSCRAMBLE_CLUE_MAX_LEN : UNSCRAMBLE_QA_MAX_LEN;
    const sanitized = sanitizeAlphaNum(value).slice(0, max);
    setPairs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: sanitized } : p))
    );
    setErrorMessage("");
    setSuccessMessage("");
  };

  const clearAll = () => {
    setPairs([emptyUnscramblePair(), emptyUnscramblePair()]);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleMinAnswersChange = (value) => {
    const raw = String(value ?? "").replace(/[^\d]/g, "");
    if (!raw) {
      setMinAnswersRequired("");
      return;
    }
    const n = Math.min(UNSCRAMBLE_MIN_ANSWERS_MAX, Math.max(1, Math.trunc(Number(raw) || 1)));
    setMinAnswersRequired(String(n));
  };

  const leftPairs = pairs.filter((_, i) => i % 2 === 0);
  const rightPairs = pairs.filter((_, i) => i % 2 === 1);

  const renderPairCard = (pair) => (
    <div
      key={pair.id}
      className={`qa-pair-card${showClue ? " qa-pair-card--with-clue" : ""}`}
    >
      <label htmlFor={`uq-${pair.id}`}>Question</label>
      <input
        id={`uq-${pair.id}`}
        type="text"
        value={pair.question}
        maxLength={UNSCRAMBLE_QA_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "question", e.target.value)}
        placeholder="Scrambled word or phrase"
      />
      <button
        type="button"
        className="qa-pair-delete"
        onClick={() => removePair(pair.id)}
        aria-label="Delete pair"
        title="Delete pair"
      >
        <i className="fa-regular fa-trash-can" />
      </button>
      <label htmlFor={`ua-${pair.id}`}>Answer</label>
      <input
        id={`ua-${pair.id}`}
        type="text"
        value={pair.answer}
        maxLength={UNSCRAMBLE_QA_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
        placeholder="Correct answer"
      />
      {showClue ? (
        <>
          <label htmlFor={`uc-${pair.id}`}>Clue</label>
          <input
            id={`uc-${pair.id}`}
            type="text"
            value={pair.clue}
            maxLength={UNSCRAMBLE_CLUE_MAX_LEN}
            onChange={(e) => updatePair(pair.id, "clue", e.target.value)}
            placeholder="Clue"
          />
        </>
      ) : null}
    </div>
  );

  return (
    <div className="themeupdate-panel-wide unscramble-panel">
      <div className="unscramble-show-clue-row">
        <label className="unscramble-toggle">
          <input
            type="checkbox"
            checked={showClue}
            onChange={(e) => {
              setShowClue(e.target.checked);
              setErrorMessage("");
              setSuccessMessage("");
            }}
          />
          <span className="unscramble-toggle-slider" />
        </label>
        <span className="unscramble-show-clue-label">Show Clue</span>
      </div>

      <p className="qa-char-limit-hint">
        * Add {UNSCRAMBLE_MIN_PAIRS}–{UNSCRAMBLE_MAX_PAIRS} complete pairs. Letters, numbers, and
        spaces only. Question and answer — {UNSCRAMBLE_QA_MAX_LEN} characters each · Clue —{" "}
        {UNSCRAMBLE_CLUE_MAX_LEN} characters.
      </p>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="qa-pairs-columns">
        <div className="qa-pairs-col">{leftPairs.map(renderPairCard)}</div>
        <div className="qa-pairs-col">{rightPairs.map(renderPairCard)}</div>
      </div>

      <div className="qa-pairs-actions">
        <button
          type="button"
          className="qa-btn qa-btn-outline"
          onClick={addPair}
          disabled={pairs.length >= UNSCRAMBLE_MAX_PAIRS}
        >
          Add Pair
        </button>
        <button
          type="button"
          className="qa-btn qa-btn-primary"
          onClick={onSavePairs}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save and Continue"}
        </button>
        <button type="button" className="qa-btn qa-btn-primary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      <div className="unscramble-min-answers">
        <label htmlFor="unscramble-min-answers">Minimum answers required for submit</label>
        <input
          id="unscramble-min-answers"
          type="number"
          min={1}
          max={UNSCRAMBLE_MIN_ANSWERS_MAX}
          value={minAnswersRequired}
          onChange={(e) => handleMinAnswersChange(e.target.value)}
        />
      </div>

      <div className="unscramble-save-bottom">
        <button
          type="button"
          className="qa-btn qa-btn-primary"
          onClick={onSaveSettings}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function PlaceholderGameTab({ title }) {
  return (
    <div className="themeupdate-empty-tab themeupdate-panel-wide">
      <h3>{title}</h3>
      <p className="themeupdate-muted">Content editor for this game will be added here.</p>
    </div>
  );
}

function StageHeadingSubheadingEditor({
  stageLabel,
  heading,
  setHeading,
  subheading,
  setSubheading,
  onSave,
  isSaving = false,
  saveLabel = "Save",
}) {
  return (
    <div className="themeupdate-panel-wide">
      <div className="themeupdate-card">
        <div className="themeupdate-card-body">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <label className="themeupdate-label">{stageLabel} heading</label>
              <input
                type="text"
                className="themeupdate-input"
                value={heading}
                maxLength={STAGE_HEADING_MAX_LEN}
                onChange={(e) => setHeading(clampStageHeading(e.target.value))}
              />
            </div>

            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <label className="themeupdate-label">{stageLabel} sub heading</label>
              <textarea
                className="themeupdate-input"
                rows={2}
                value={subheading}
                maxLength={STAGE_SUBHEADING_MAX_LEN}
                onChange={(e) => setSubheading(clampStageSubheading(e.target.value))}
              />
            </div>
          </div>

          <p className="themeupdate-muted" style={{ marginTop: 8 }}>
            * Heading — max {STAGE_HEADING_MAX_LEN} characters · Sub heading — max{" "}
            {STAGE_SUBHEADING_MAX_LEN} characters.
          </p>

          {onSave ? (
            <div className="stage-heading-save-wrap">
              <button
                type="button"
                className="qa-btn qa-btn-primary"
                onClick={onSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving…" : saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const activeTab = getGameTabFromSearch(searchParams);
  const [pairs, setPairs] = useState([emptyPair(), emptyPair()]);
  const [unscramblePairs, setUnscramblePairs] = useState([emptyUnscramblePair(), emptyUnscramblePair()]);
  const [showClue, setShowClue] = useState(false);
  const [minAnswersRequired, setMinAnswersRequired] = useState("1");
  const [isSaving, setIsSaving] = useState(false);
  const [daErrorMessage, setDaErrorMessage] = useState("");
  const [daSuccessMessage, setDaSuccessMessage] = useState("");
  const [unscrambleErrorMessage, setUnscrambleErrorMessage] = useState("");
  const [unscrambleSuccessMessage, setUnscrambleSuccessMessage] = useState("");
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizErrorMessage, setQuizErrorMessage] = useState("");
  const [quizSuccessMessage, setQuizSuccessMessage] = useState("");
  const [dosItems, setDosItems] = useState([]);
  const [dontItems, setDontItems] = useState([]);
  const [dosDontErrorMessage, setDosDontErrorMessage] = useState("");
  const [dosDontSuccessMessage, setDosDontSuccessMessage] = useState("");
  const [wordPuzzleEntries, setWordPuzzleEntries] = useState([]);
  const [hintDisplayMode, setHintDisplayMode] = useState("show_full_word");
  const [wpErrorMessage, setWpErrorMessage] = useState("");
  const [wpSuccessMessage, setWpSuccessMessage] = useState("");

  // Stage title + subtitle (admin-configurable per mini-game stage).
  const [downAndAcrossHeading, setDownAndAcrossHeading] = useState("");
  const [downAndAcrossSubheading, setDownAndAcrossSubheading] = useState(
    DEFAULT_SUBTITLE_STAGE_1
  );
  const [wordPuzzleHeading, setWordPuzzleHeading] = useState("");
  const [wordPuzzleSubheading, setWordPuzzleSubheading] = useState(
    DEFAULT_SUBTITLE_STAGE_2
  );
  const [unscrambleHeading, setUnscrambleHeading] = useState("");
  const [unscrambleSubheading, setUnscrambleSubheading] = useState(
    DEFAULT_SUBTITLE_STAGE_3
  );
  const [quizHeading, setQuizHeading] = useState("");
  const [quizSubheading, setQuizSubheading] = useState(DEFAULT_SUBTITLE_STAGE_4);
  const [dosDontHeading, setDosDontHeading] = useState("");
  const [dosDontSubheading, setDosDontSubheading] = useState(
    DEFAULT_SUBTITLE_STAGE_5
  );
  const [downAndAcrossRules, setDownAndAcrossRules] = useState([""]);
  const [wordPuzzleRules, setWordPuzzleRules] = useState([""]);
  const [unscrambleRules, setUnscrambleRules] = useState([""]);
  const [quizRules, setQuizRules] = useState([""]);
  const [dosDontRules, setDosDontRules] = useState([""]);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setPairs(normalizePairsFromTheme(data.questions_and_answers));
    setUnscramblePairs(normalizeUnscrambleFromTheme(data.unscramble));
    setShowClue(parseShowClue(data.show_clue));
    const minWords = getUnscrambleMinAnswersForAdmin(data);
    setMinAnswersRequired(
      String(Math.min(UNSCRAMBLE_MIN_ANSWERS_MAX, minWords))
    );
    setQuizQuestions(normalizeQuizFromTheme(data.quiz));
    setDosItems(normalizeStatementsFromTheme(data.dos));
    setDontItems(normalizeStatementsFromTheme(data.donts));
    setWordPuzzleEntries(normalizeWordEntriesFromTheme(data.wordpuzzle));
    setHintDisplayMode(parseHintDisplayMode(data));

    const titleFallback = String(
      data.landing_page_title ?? data.main_title ?? data.themename ?? ""
    ).trim();

    setDownAndAcrossHeading(
      clampStageHeading(data.downandacross_title ?? titleFallback ?? "Down and Across")
    );
    setDownAndAcrossSubheading(
      clampStageSubheading(data.downandacross_subtitle ?? DEFAULT_SUBTITLE_STAGE_1)
    );

    setWordPuzzleHeading(
      clampStageHeading(data.wordpuzzle_title ?? titleFallback ?? "Word Search")
    );
    setWordPuzzleSubheading(
      clampStageSubheading(data.wordpuzzle_subtitle ?? DEFAULT_SUBTITLE_STAGE_2)
    );

    setUnscrambleHeading(
      clampStageHeading(data.unscramble_title ?? titleFallback ?? "Unscramble")
    );
    setUnscrambleSubheading(
      clampStageSubheading(
        data.unscramble_subtitle ??
          data.unscramble_instructions ??
          DEFAULT_SUBTITLE_STAGE_3
      )
    );

    setQuizHeading(clampStageHeading(data.quiz_title ?? titleFallback ?? "Quiz"));
    setQuizSubheading(clampStageSubheading(data.quiz_subtitle ?? DEFAULT_SUBTITLE_STAGE_4));

    setDosDontHeading(
      clampStageHeading(data.dos_dont_title ?? titleFallback ?? "BUILDING YOUR BRAND")
    );
    setDosDontSubheading(
      clampStageSubheading(data.dos_dont_subtitle ?? DEFAULT_SUBTITLE_STAGE_5)
    );

    const legacyRules = Array.isArray(data.rules)
      ? data.rules
      : data.rules_text
        ? [String(data.rules_text)]
        : [];
    setDownAndAcrossRules(
      normalizeRulesFromTheme(
        data.downandacross_rules ?? (legacyRules.length ? legacyRules : undefined)
      )
    );
    setWordPuzzleRules(normalizeRulesFromTheme(data.wordpuzzle_rules));
    setUnscrambleRules(normalizeRulesFromTheme(data.unscramble_rules));
    setQuizRules(normalizeRulesFromTheme(data.quiz_rules));
    setDosDontRules(normalizeRulesFromTheme(data.dos_dont_rules));
  }, [data]);

  const layout = useMemo(() => {
    const words = buildWordsFromPairs(pairs);
    if (words.length < MIN_PAIRS) return null;
    try {
      const wordsCopy = words.map((w) => ({ ...w }));
      return generateLayout(wordsCopy);
    } catch {
      return null;
    }
  }, [pairs]);

  const layoutError = useMemo(() => {
    const words = buildWordsFromPairs(pairs);
    if (words.length < MIN_PAIRS) return null;
    if (!layout?.table?.length) {
      return "Could not generate a crossword from these answers. Try shorter answers or different words.";
    }
    const placed = Object.values(layout.result || {}).filter(
      (w) => w.orientation && w.orientation !== "none"
    );
    if (placed.length < words.length) {
      return `Only ${placed.length} of ${words.length} answers were placed on the grid. Some words may be too long or incompatible.`;
    }
    return null;
  }, [pairs, layout]);

  const handleSaveDownAndAcross = async () => {
    const stageMeta = validateStageHeadingSubheadingAndRules(
      downAndAcrossHeading,
      downAndAcrossSubheading,
      downAndAcrossRules,
      "Down and Across"
    );
    if (!stageMeta.ok) {
      setDaErrorMessage(stageMeta.message);
      setDaSuccessMessage("");
      return;
    }

    const filled = pairs
      .map((p) => ({
        question: p.question.trim(),
        answer: p.answer.trim(),
      }))
      .filter((p) => p.question || p.answer);

    const complete = filled.filter((p) => p.question && p.answer);

    if (complete.length < MIN_PAIRS) {
      setDaErrorMessage(`Please add at least ${MIN_PAIRS} complete question/answer pairs before saving.`);
      setDaSuccessMessage("");
      return;
    }

    if (complete.length > MAX_PAIRS) {
      setDaErrorMessage(`You can save up to ${MAX_PAIRS} question/answer pairs.`);
      setDaSuccessMessage("");
      return;
    }

    const hasEmpty = filled.some((p) => !p.question || !p.answer);
    if (hasEmpty) {
      setDaErrorMessage("Please fill in all fields or remove incomplete pairs.");
      setDaSuccessMessage("");
      return;
    }

    const questionTooLong = complete.find((p) => p.question.length > QUESTION_MAX_LEN);
    if (questionTooLong) {
      setDaErrorMessage(`Each question must be ${QUESTION_MAX_LEN} characters or fewer.`);
      return;
    }

    const answerTooLong = complete.find((p) => p.answer.length > ANSWER_MAX_LEN);
    if (answerTooLong) {
      setDaErrorMessage(`Each answer must be ${ANSWER_MAX_LEN} characters or fewer.`);
      return;
    }

    if (layoutError && !layout?.table?.length) {
      setDaErrorMessage(layoutError);
      return;
    }

    const crosswordLayout = serializeCrosswordLayout(layout);

    setIsSaving(true);
    setDaErrorMessage("");
    setDaSuccessMessage("");

    try {
      const payload = {
        questions_and_answers: complete,
        downandacross_title: stageMeta.heading,
        downandacross_subtitle: stageMeta.subheading,
        downandacross_rules: stageMeta.rules,
      };
      if (crosswordLayout) {
        payload.crossword_layout = crosswordLayout;
      }

      await updateThemeData({
        payload: {
          data: payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Down and Across data saved to theme.", "success");
      setDaSuccessMessage("Saved to theme.");
    } catch (err) {
      setDaErrorMessage(err?.message || "Failed to save data.");
    } finally {
      setIsSaving(false);
    }
  };

  const validateUnscramblePairs = () => {
    const filled = unscramblePairs
      .map((p) => ({
        question: sanitizeAlphaNum(p.question).trim(),
        answer: sanitizeAlphaNum(p.answer).trim(),
        clue: sanitizeAlphaNum(p.clue).trim(),
      }))
      .filter((p) => p.question || p.answer || p.clue);

    const complete = filled.filter((p) => p.question && p.answer);

    if (complete.length < UNSCRAMBLE_MIN_PAIRS) {
      return {
        ok: false,
        message: `Please add at least ${UNSCRAMBLE_MIN_PAIRS} complete question/answer pairs before saving.`,
      };
    }

    if (complete.length > UNSCRAMBLE_MAX_PAIRS) {
      return {
        ok: false,
        message: `You can save up to ${UNSCRAMBLE_MAX_PAIRS} question/answer pairs.`,
      };
    }

    const hasEmpty = filled.some((p) => !p.question || !p.answer);
    if (hasEmpty) {
      return { ok: false, message: "Please fill in all fields or remove incomplete pairs." };
    }

    const tooLongQa = complete.find(
      (p) => p.question.length > UNSCRAMBLE_QA_MAX_LEN || p.answer.length > UNSCRAMBLE_QA_MAX_LEN
    );
    if (tooLongQa) {
      return {
        ok: false,
        message: `Each question and answer must be ${UNSCRAMBLE_QA_MAX_LEN} characters or fewer.`,
      };
    }

    const tooLongClue = showClue && complete.find((p) => p.clue.length > UNSCRAMBLE_CLUE_MAX_LEN);
    if (tooLongClue) {
      return {
        ok: false,
        message: `Each clue must be ${UNSCRAMBLE_CLUE_MAX_LEN} characters or fewer.`,
      };
    }

    if (showClue && complete.some((p) => !p.clue)) {
      return { ok: false, message: "Please fill in all clue fields or turn off Show Clue." };
    }

    return {
      ok: true,
      complete: complete.map((p) => ({
        question: p.question,
        answer: p.answer,
        clue: showClue ? p.clue : p.clue || "",
      })),
    };
  };

  const handleSaveUnscramblePairs = async () => {
    const stageMeta = validateStageHeadingSubheadingAndRules(
      unscrambleHeading,
      unscrambleSubheading,
      unscrambleRules,
      "Unscramble"
    );
    if (!stageMeta.ok) {
      setUnscrambleErrorMessage(stageMeta.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    const validation = validateUnscramblePairs();
    if (!validation.ok) {
      setUnscrambleErrorMessage(validation.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setUnscrambleErrorMessage("");
    setUnscrambleSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            unscramble: validation.complete,
            unscramble_title: stageMeta.heading,
            unscramble_subtitle: stageMeta.subheading,
            // Backward-compat alias used by the stage renderer.
            unscramble_instructions: stageMeta.subheading,
            unscramble_rules: stageMeta.rules,
            show_clue: showClue ? "true" : "false",
            question_count: validation.complete.length,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Unscramble words saved to theme.", "success");
      setUnscrambleSuccessMessage("Pairs saved to theme.");
    } catch (err) {
      setUnscrambleErrorMessage(err?.message || "Failed to save data.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveUnscrambleSettings = async () => {
    const stageMeta = validateStageHeadingSubheadingAndRules(
      unscrambleHeading,
      unscrambleSubheading,
      unscrambleRules,
      "Unscramble"
    );
    if (!stageMeta.ok) {
      setUnscrambleErrorMessage(stageMeta.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    const validation = validateUnscramblePairs();
    if (!validation.ok) {
      setUnscrambleErrorMessage(validation.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    const minVal = Math.trunc(Number(minAnswersRequired) || 0);
    if (!Number.isFinite(minVal) || minVal < 1) {
      setUnscrambleErrorMessage("Minimum answers required must be at least 1.");
      setUnscrambleSuccessMessage("");
      return;
    }

    if (minVal > validation.complete.length) {
      setUnscrambleErrorMessage(
        `Minimum answers cannot be more than the number of pairs (${validation.complete.length}).`
      );
      setUnscrambleSuccessMessage("");
      return;
    }

    if (minVal > UNSCRAMBLE_MIN_ANSWERS_MAX) {
      setUnscrambleErrorMessage(`Minimum answers cannot exceed ${UNSCRAMBLE_MIN_ANSWERS_MAX}.`);
      return;
    }

    setIsSaving(true);
    setUnscrambleErrorMessage("");
    setUnscrambleSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            words: minVal,
            unscramble_min_answers: minVal,
            unscramble_title: stageMeta.heading,
            unscramble_subtitle: stageMeta.subheading,
            // Backward-compat alias used by the stage renderer.
            unscramble_instructions: stageMeta.subheading,
            unscramble_rules: stageMeta.rules,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Unscramble settings saved.", "success");
      setUnscrambleSuccessMessage("Settings saved.");
    } catch (err) {
      setUnscrambleErrorMessage(err?.message || "Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQuizHeadings = async () => {
    const stageMeta = validateStageHeadingSubheading(
      quizHeading,
      quizSubheading,
      "Quiz"
    );
    if (!stageMeta.ok) {
      setQuizErrorMessage(stageMeta.message);
      setQuizSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz_title: stageMeta.heading,
            quiz_subtitle: stageMeta.subheading,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Quiz heading saved to theme.", "success");
      setQuizSuccessMessage("Heading saved to theme.");
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save quiz heading.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQuizRules = async () => {
    const rulesCheck = validateStageRules(quizRules, "Quiz");
    if (!rulesCheck.ok) {
      setQuizErrorMessage(rulesCheck.message);
      setQuizSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz_rules: rulesCheck.rules,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Quiz rules saved to theme.", "success");
      setQuizSuccessMessage("Rules saved to theme.");
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save quiz rules.");
    } finally {
      setIsSaving(false);
    }
  };

  const persistQuizQuestions = async (nextQuestions) => {
    const validation = validateQuizList(
      nextQuestions.map(({ id, ...rest }) => rest)
    );
    if (!validation.ok) {
      throw new Error(validation.message);
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz: validation.data,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setQuizQuestions(
        nextQuestions.map((q, i) => ({
          id: q.id || newPairId("quiz"),
          ...validation.data[i],
        }))
      );
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save quiz data.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDosAndDont = async () => {
    const stageMeta = validateStageHeadingSubheadingAndRules(
      dosDontHeading,
      dosDontSubheading,
      dosDontRules,
      "Do's and Don'ts"
    );
    if (!stageMeta.ok) {
      setDosDontErrorMessage(stageMeta.message);
      setDosDontSuccessMessage("");
      return;
    }

    const dosCheck = validateStatementsList(dosItems, "Do");
    if (!dosCheck.ok) {
      setDosDontErrorMessage(dosCheck.message);
      setDosDontSuccessMessage("");
      return;
    }

    const dontCheck = validateStatementsList(dontItems, "Don't");
    if (!dontCheck.ok) {
      setDosDontErrorMessage(dontCheck.message);
      setDosDontSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setDosDontErrorMessage("");
    setDosDontSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            dos: dosCheck.data,
            donts: dontCheck.data,
            dos_dont_title: stageMeta.heading,
            dos_dont_subtitle: stageMeta.subheading,
            dos_dont_rules: stageMeta.rules,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Do's and Don'ts saved to theme.", "success");
      setDosDontSuccessMessage("Saved to theme.");
    } catch (err) {
      setDosDontErrorMessage(err?.message || "Failed to save data.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWordPuzzle = async () => {
    const stageMeta = validateStageHeadingSubheadingAndRules(
      wordPuzzleHeading,
      wordPuzzleSubheading,
      wordPuzzleRules,
      "Word Puzzle"
    );
    if (!stageMeta.ok) {
      setWpErrorMessage(stageMeta.message);
      setWpSuccessMessage("");
      return;
    }

    const validation = validateWordPuzzleEntries(wordPuzzleEntries);
    if (!validation.ok) {
      setWpErrorMessage(validation.message);
      setWpSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setWpErrorMessage("");
    setWpSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            wordpuzzle: {
              hint_display_mode: hintDisplayMode,
              words: validation.data,
            },
            wordpuzzle_title: stageMeta.heading,
            wordpuzzle_subtitle: stageMeta.subheading,
            wordpuzzle_rules: stageMeta.rules,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Word puzzle saved to theme.", "success");
      setWpSuccessMessage("Saved to theme.");
    } catch (err) {
      setWpErrorMessage(err?.message || "Failed to save word puzzle.");
    } finally {
      setIsSaving(false);
    }
  };

  const clearTabMessages = () => {
    setDaErrorMessage("");
    setDaSuccessMessage("");
    setUnscrambleErrorMessage("");
    setUnscrambleSuccessMessage("");
    setQuizErrorMessage("");
    setQuizSuccessMessage("");
    setDosDontErrorMessage("");
    setDosDontSuccessMessage("");
    setWpErrorMessage("");
    setWpSuccessMessage("");
  };

  const changeTab = (tabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tabId === DEFAULT_GAME_TAB) {
          next.delete(GAME_TAB_PARAM);
        } else {
          next.set(GAME_TAB_PARAM, tabId);
        }
        return next;
      },
      { replace: true }
    );
    clearTabMessages();
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <header className="themeupdate-header themeupdate-panel-wide">
        <h1 className="themeupdate-title">Game Content</h1>
        <p className="themeupdate-muted">Manage questions and content for each mini-game in this theme.</p>
      </header>

      <nav className="themeupdate-tabs" aria-label="Game tabs">
        {GAME_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`themeupdate-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => changeTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === "downAndAcross" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Stage 1"
            heading={downAndAcrossHeading}
            setHeading={setDownAndAcrossHeading}
            subheading={downAndAcrossSubheading}
            setSubheading={setDownAndAcrossSubheading}
          />
          <StageRulesEditor
            stageLabel="Stage 1"
            rules={downAndAcrossRules}
            setRules={setDownAndAcrossRules}
          />
          <DownAndAcrossTab
            pairs={pairs}
            setPairs={setPairs}
            layout={layout}
            layoutError={layoutError}
            isSaving={isSaving}
            onSave={handleSaveDownAndAcross}
            errorMessage={daErrorMessage}
            successMessage={daSuccessMessage}
            setErrorMessage={setDaErrorMessage}
            setSuccessMessage={setDaSuccessMessage}
          />
        </>
      )}

      {activeTab === "wordpuzzle" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Stage 2"
            heading={wordPuzzleHeading}
            setHeading={setWordPuzzleHeading}
            subheading={wordPuzzleSubheading}
            setSubheading={setWordPuzzleSubheading}
          />
          <StageRulesEditor
            stageLabel="Stage 2"
            rules={wordPuzzleRules}
            setRules={setWordPuzzleRules}
          />
          <WordPuzzleTab
            entries={wordPuzzleEntries}
            setEntries={setWordPuzzleEntries}
            hintDisplayMode={hintDisplayMode}
            setHintDisplayMode={setHintDisplayMode}
            isSaving={isSaving}
            onSave={handleSaveWordPuzzle}
            errorMessage={wpErrorMessage}
            successMessage={wpSuccessMessage}
            setErrorMessage={setWpErrorMessage}
            setSuccessMessage={setWpSuccessMessage}
            newEntryId={() => newPairId("wp")}
          />
        </>
      )}
      {activeTab === "unscramble" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Stage 3"
            heading={unscrambleHeading}
            setHeading={setUnscrambleHeading}
            subheading={unscrambleSubheading}
            setSubheading={setUnscrambleSubheading}
          />
          <StageRulesEditor
            stageLabel="Stage 3"
            rules={unscrambleRules}
            setRules={setUnscrambleRules}
          />
          <UnscrambleTab
            pairs={unscramblePairs}
            setPairs={setUnscramblePairs}
            showClue={showClue}
            setShowClue={setShowClue}
            minAnswersRequired={minAnswersRequired}
            setMinAnswersRequired={setMinAnswersRequired}
            isSaving={isSaving}
            onSavePairs={handleSaveUnscramblePairs}
            onSaveSettings={handleSaveUnscrambleSettings}
            errorMessage={unscrambleErrorMessage}
            successMessage={unscrambleSuccessMessage}
            setErrorMessage={setUnscrambleErrorMessage}
            setSuccessMessage={setUnscrambleSuccessMessage}
          />
        </>
      )}
      {activeTab === "quiz" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Stage 4"
            heading={quizHeading}
            setHeading={setQuizHeading}
            subheading={quizSubheading}
            setSubheading={setQuizSubheading}
            onSave={handleSaveQuizHeadings}
            isSaving={isSaving}
            saveLabel="Save heading"
          />
          <StageRulesEditor
            stageLabel="Stage 4"
            rules={quizRules}
            setRules={setQuizRules}
            onSave={handleSaveQuizRules}
            isSaving={isSaving}
            saveLabel="Save rules"
          />
          <QuizTab
            questions={quizQuestions}
            setQuestions={setQuizQuestions}
            isSaving={isSaving}
            onPersist={persistQuizQuestions}
            errorMessage={quizErrorMessage}
            successMessage={quizSuccessMessage}
            setErrorMessage={setQuizErrorMessage}
            setSuccessMessage={setQuizSuccessMessage}
            newQuestionId={() => newPairId("quiz")}
          />
        </>
      )}
      {activeTab === "dosAndDont" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Stage 5"
            heading={dosDontHeading}
            setHeading={setDosDontHeading}
            subheading={dosDontSubheading}
            setSubheading={setDosDontSubheading}
          />
          <StageRulesEditor
            stageLabel="Stage 5"
            rules={dosDontRules}
            setRules={setDosDontRules}
          />
          <DosAndDontTab
            dosItems={dosItems}
            setDosItems={setDosItems}
            dontItems={dontItems}
            setDontItems={setDontItems}
            isSaving={isSaving}
            onSave={handleSaveDosAndDont}
            errorMessage={dosDontErrorMessage}
            successMessage={dosDontSuccessMessage}
            setErrorMessage={setDosDontErrorMessage}
            setSuccessMessage={setDosDontSuccessMessage}
            newStatementId={() => newPairId("dd")}
          />
        </>
      )}
    </div>
  );
};

export default ThemeUpdate;
